import { apiClient } from './api';

function unwrapData<T>(res: { success: boolean; data?: T; message?: string; error?: string }, fallbackMsg: string): T {
  if (!res.success || res.data === undefined) {
    throw new Error(res.message || res.error || fallbackMsg);
  }
  return res.data;
}

export type BaleReadStatus = 'unread' | 'read' | '';

export type BaleConversationRow = {
  id: string;
  baleChatId: string;
  baleUserId: string;
  displayName: string;
  username: string;
  lastMessageText: string;
  lastMessageAt: string;
  unreadCount: number;
  createdAt: string;
};

export type BaleMessageRow = {
  id: string;
  baleMessageId: string;
  direction: 'in' | 'out';
  text: string;
  contentType: string;
  createdAt: string;
};

type PageResult<T> = {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

function mapConversation(raw: Record<string, unknown>): BaleConversationRow {
  return {
    id: String(raw.id ?? ''),
    baleChatId: String(raw.baleChatId ?? ''),
    baleUserId: String(raw.baleUserId ?? ''),
    displayName: String(raw.displayName ?? ''),
    username: String(raw.username ?? ''),
    lastMessageText: String(raw.lastMessageText ?? ''),
    lastMessageAt: String(raw.lastMessageAt ?? ''),
    unreadCount: Number(raw.unreadCount ?? 0),
    createdAt: String(raw.createdAt ?? ''),
  };
}

function mapMessage(raw: Record<string, unknown>): BaleMessageRow {
  return {
    id: String(raw.id ?? ''),
    baleMessageId: String(raw.baleMessageId ?? ''),
    direction: (raw.direction === 'out' ? 'out' : 'in') as 'in' | 'out',
    text: String(raw.text ?? ''),
    contentType: String(raw.contentType ?? 'text'),
    createdAt: String(raw.createdAt ?? ''),
  };
}

export const baleService = {
  async listConversations(params: { page?: number; limit?: number; status?: BaleReadStatus } = {}): Promise<PageResult<BaleConversationRow>> {
    const res = await apiClient.get<PageResult<Record<string, unknown>>>('/admin/bale/conversations', { params });
    const page = unwrapData(res, 'خطا در دریافت گفتگوها');
    return {
      ...page,
      data: (page.data ?? []).map(mapConversation),
    };
  },
  async getConversation(id: string): Promise<BaleConversationRow> {
    const res = await apiClient.get<Record<string, unknown>>(`/admin/bale/conversations/${id}`);
    return mapConversation(unwrapData(res, 'گفتگو یافت نشد'));
  },
  async listMessages(conversationId: string, params: { page?: number; limit?: number } = {}): Promise<PageResult<BaleMessageRow>> {
    const res = await apiClient.get<PageResult<Record<string, unknown>>>(`/admin/bale/conversations/${conversationId}/messages`, { params });
    const page = unwrapData(res, 'خطا در دریافت پیام‌ها');
    return {
      ...page,
      data: (page.data ?? []).map(mapMessage),
    };
  },
  async markRead(id: string) {
    const res = await apiClient.patch(`/admin/bale/conversations/${id}/read`);
    unwrapData(res, 'خطا در علامت‌گذاری');
  },
  async reply(id: string, text: string): Promise<BaleMessageRow> {
    const res = await apiClient.post<Record<string, unknown>>(`/admin/bale/conversations/${id}/reply`, { text });
    return mapMessage(unwrapData(res, 'خطا در ارسال پاسخ'));
  },
  async editMessage(conversationId: string, messageId: string, text: string): Promise<BaleMessageRow> {
    const res = await apiClient.patch<Record<string, unknown>>(
      `/admin/bale/conversations/${conversationId}/messages/${messageId}`,
      { text }
    );
    return mapMessage(unwrapData(res, 'خطا در ویرایش پیام'));
  },
};
